import React from 'react';
import { STRRoom } from './types';
import { formatDate, formatNumber } from './utils';

interface Props {
  room: STRRoom | null;
  selStart: Date | null;
  selEnd: Date | null;
  onOpenBookingModal: (isExtension: boolean) => void;
  onOpenBlockModal: () => void;
  onUnblock: () => void;
  onClearSelection: () => void;
}

function isRangeBlocked(room: STRRoom, startStr: string, endStr: string): boolean {
  const d = new Date(startStr + 'T00:00:00');
  const end = new Date(endStr + 'T00:00:00');
  while (d <= end) {
    const ds = d.toISOString().slice(0, 10);
    const found = (room.blocked_dates || []).some((bl) => ds >= bl.start && ds <= bl.end);
    if (!found) return false;
    d.setDate(d.getDate() + 1);
  }
  return true;
}

export default function Sidebar({ room, selStart, selEnd, onOpenBookingModal, onOpenBlockModal, onUnblock, onClearSelection }: Props) {
  // No date selected: show hint
  if (!selStart) {
    return (
      <div style={{ textAlign: 'center', padding: '16px 0', color: '#999', fontSize: 13, lineHeight: 1.8 }}>
        날짜를 클릭하여 범위를 선택하세요<br />
        <span style={{ fontSize: 12 }}>시작일 클릭 → 종료일 클릭</span>
      </div>
    );
  }

  // Start selected, waiting for end
  if (!selEnd) {
    return (
      <div style={{ padding: '12px 0' }}>
        <div style={{ fontSize: 13, padding: 10, background: '#e3f2fd', borderRadius: 6, marginBottom: 8 }}>
          <div style={{ fontWeight: 500 }}>{formatDate(selStart)} 선택됨</div>
          <div style={{ fontSize: 12, color: '#666', marginTop: 2 }}>종료일을 클릭하세요</div>
        </div>
        <button
          onClick={onClearSelection}
          style={{ width: '100%', padding: '7px', border: '1px solid #ccc', background: 'white', borderRadius: 4, cursor: 'pointer', fontSize: 12 }}
        >
          선택 해제
        </button>
      </div>
    );
  }

  // Both dates selected
  const days = Math.round((selEnd.getTime() - selStart.getTime()) / 86400000) + 1;
  const warn = '';

  const dailyRent = (room?.weekly_rent || 0) / 7;
  const dailyMaint = (room?.weekly_maintenance || 0) / 7;
  const totalRent = Math.round(dailyRent * days);
  const totalMaint = Math.round(dailyMaint * days);
  const totalClean = room?.weekly_cleaning || 0;
  const subtotal = totalRent + totalMaint + totalClean;
  const platformFee = Math.round(subtotal * 0.033);
  const totalPrice = subtotal - platformFee;

  const startStr = formatDate(selStart);
  const endStr = formatDate(selEnd);
  let hasConflict = false;
  let hasBlockConflict = false;

  if (room) {
    (room.bookings || []).forEach((b) => {
      if (b.status === 'cancelled') return;
      if (startStr < b.check_out && endStr > b.check_in) hasConflict = true;
    });
    (room.blocked_dates || []).forEach((bl) => {
      if (startStr < bl.end && endStr > bl.start) hasBlockConflict = true;
    });
  }

  const isAllBlocked = room ? isRangeBlocked(room, startStr, endStr) : false;

  return (
    <div style={{ padding: '12px 0' }}>
      {/* Selection info */}
      <div style={{
        fontSize: 12, padding: 10, borderRadius: 6, marginBottom: 10,
        background: warn ? '#fff3e0' : '#f5f5f5',
      }}>
        <div style={{ fontWeight: 600, fontSize: 13 }}>
          {formatDate(selStart)} ~ {formatDate(selEnd)}
        </div>
        <div style={{ color: '#666', marginTop: 2 }}>
          {days}일
          {warn && <span style={{ color: '#e65100' }}> {warn}</span>}
        </div>
      </div>

      {/* Price panel */}
      <div style={{ fontSize: 13, marginBottom: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0' }}>
          <span style={{ color: '#888' }}>기간</span><span style={{ fontWeight: 500 }}>{days}일</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0' }}>
          <span style={{ color: '#888' }}>임대료</span><span style={{ fontWeight: 500 }}>{formatNumber(totalRent)}원</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0' }}>
          <span style={{ color: '#888' }}>관리비</span><span style={{ fontWeight: 500 }}>{formatNumber(totalMaint)}원</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0' }}>
          <span style={{ color: '#888' }}>청소비</span><span style={{ fontWeight: 500 }}>{formatNumber(totalClean)}원</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', color: '#e65100' }}>
          <span>수수료</span><span>-{formatNumber(platformFee)}원</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0 6px', borderTop: '1px solid #e2e5e9', marginTop: 8, fontWeight: 600, fontSize: 14 }}>
          <span>합계</span><span>{formatNumber(totalPrice)}원</span>
        </div>
        <div style={{ marginTop: 8, padding: '8px 12px', background: '#fff3e0', borderRadius: 6, fontSize: 12, color: '#e65100' }}>
          + 보증금 {formatNumber(room?.deposit || 330000)}원 (별도)
        </div>
      </div>

      {/* Action buttons */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {!hasConflict && !hasBlockConflict && !warn && (
          <button
            onClick={() => onOpenBookingModal(false)}
            style={{ padding: '8px', background: '#1976d2', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 13 }}
          >
            예약 추가
          </button>
        )}
        {!hasConflict && !isAllBlocked && !warn && (
          <button
            onClick={onOpenBlockModal}
            style={{ padding: '8px', background: 'white', color: '#333', border: '1px solid #ccc', borderRadius: 4, cursor: 'pointer', fontSize: 13 }}
          >
            예약불가 설정
          </button>
        )}
        {isAllBlocked && (
          <button
            onClick={onUnblock}
            style={{ padding: '8px', background: 'white', color: '#e65100', border: '1px solid #e65100', borderRadius: 4, cursor: 'pointer', fontSize: 13 }}
          >
            예약불가 해제
          </button>
        )}
        <button
          onClick={onClearSelection}
          style={{ padding: '8px', background: 'white', color: '#666', border: '1px solid #ccc', borderRadius: 4, cursor: 'pointer', fontSize: 13 }}
        >
          선택 해제
        </button>
      </div>
    </div>
  );
}
